import Drawer from '@/features/cbam-reporting/components/Drawer'
import InfoDisclosure, {
  infoContentComponents,
} from '@/features/cbam-simulations-v2/components/InfoDisclosure'
import EuaCertificatePriceTable, {
  type EditableEuaQuarterField,
} from '@/features/cbam-simulations-v2/components/EuaCertificatePriceTable'
import useI18n from '@/shared/hooks/usei18n'
import {
  euaQuarterValueToPayloadString,
  getModifiersFormStateFromCbamModifiers,
  serializeModifiersForChangeDetection,
  type ModifiersFormState,
} from '@/shared/cbamModifiers/modifiersFormState'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import {
  Alert,
  Button,
  GenericDialog,
  H2,
  IconButton,
  InfoTooltip,
} from '@/common'
import { RightArrowIcon, SyncIcon } from '@/common/components/icons'
import FieldRangeCaret from '@/common/components/FieldRangeCaret/FieldRangeCaret'
import {
  useEuaEmissionPriceOverviewByQuarter,
  useGetOfficial2026EuaEmissionPrice,
} from '@/shared/hooks/euaEmissionPriceOverview'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Trans } from 'react-i18next'

function isCscfLockedForYear(year: number): boolean {
  return year >= 2026 && year <= 2030
}

function appliedCscfFromVariable(cscfVariable: number): number {
  return 100 - cscfVariable
}

export type EditCbamModifiersDrawerProps = {
  isOpen: boolean
  close: () => void
  /** Reporting or simulation year (EUA overview / certificate table). */
  year: number
  /** Modifiers shown when the drawer opens or when `syncSource` changes while open. */
  initialModifiers: CbamModifiers
  /**
   * When this identity changes (e.g. report or simulation object), the form resets.
   * Pass the same entity you derive `initialModifiers` from.
   */
  syncSource: unknown
  isSaving: boolean
  onSave: (modifiers: CbamModifiers) => Promise<void>
}

export default function EditCbamModifiersDrawer({
  isOpen,
  close,
  year,
  initialModifiers,
  syncSource,
  isSaving,
  onSave,
}: EditCbamModifiersDrawerProps) {
  const { t } = useI18n()
  const isCscfLocked = useMemo(() => isCscfLockedForYear(year), [year])

  const [modifiers, setModifiers] = useState<ModifiersFormState>(() =>
    getModifiersFormStateFromCbamModifiers(initialModifiers)
  )
  const [cscfVariable, setCscfVariable] = useState(() => {
    if (isCscfLockedForYear(year)) {
      return 0
    }
    const initial = getModifiersFormStateFromCbamModifiers(initialModifiers)
    return 100 - (parseFloat(initial.cscfPercentage) || 100)
  })
  const [initialSerializedModifiers, setInitialSerializedModifiers] = useState(
    () => {
      const initial = getModifiersFormStateFromCbamModifiers(initialModifiers)
      const appliedCscf = isCscfLockedForYear(year)
        ? 100
        : parseFloat(initialModifiers.cscfPercentage ?? '100') || 100
      return serializeModifiersForChangeDetection(initial, appliedCscf)
    }
  )
  const [isConfirmDiscardDialogOpen, setIsConfirmDiscardDialogOpen] =
    useState(false)

  const { refetch: refetchEuaByQuarter } =
    useEuaEmissionPriceOverviewByQuarter(year)
  const {
    refetch: refetchOfficialQ1Price,
    isFetching: isFetchingOfficialQ1Price,
  } = useGetOfficial2026EuaEmissionPrice('Q1', { enabled: false })
  const {
    refetch: refetchOfficialQ2Price,
    isFetching: isFetchingOfficialQ2Price,
  } = useGetOfficial2026EuaEmissionPrice('Q2', { enabled: false })
  const {
    refetch: refetchOfficialQ3Price,
    isFetching: isFetchingOfficialQ3Price,
  } = useGetOfficial2026EuaEmissionPrice('Q3', { enabled: false })

  const applyingOfficialQuarter = isFetchingOfficialQ1Price
    ? 'Q1'
    : isFetchingOfficialQ2Price
      ? 'Q2'
      : isFetchingOfficialQ3Price
        ? 'Q3'
        : null

  useEffect(() => {
    if (isOpen) {
      const initial = getModifiersFormStateFromCbamModifiers(initialModifiers)
      setModifiers(initial)
      if (isCscfLocked) {
        setCscfVariable(0)
        setInitialSerializedModifiers(
          serializeModifiersForChangeDetection(initial, 100)
        )
      } else {
        const base = 100
        const applied = parseFloat(initial.cscfPercentage) || base
        setCscfVariable(base - applied)
        setInitialSerializedModifiers(
          serializeModifiersForChangeDetection(initial, applied)
        )
      }
      setIsConfirmDiscardDialogOpen(false)
    }
  }, [isOpen, syncSource, initialModifiers, isCscfLocked])

  const appliedCscf = useMemo(
    () => (isCscfLocked ? 100 : appliedCscfFromVariable(cscfVariable)),
    [cscfVariable, isCscfLocked]
  )
  const hasUnsavedChanges = useMemo(
    () =>
      serializeModifiersForChangeDetection(modifiers, appliedCscf) !==
      initialSerializedModifiers,
    [modifiers, appliedCscf, initialSerializedModifiers]
  )

  const requestClose = useCallback(() => {
    if (isSaving) {
      return
    }
    if (!hasUnsavedChanges) {
      close()
      return
    }
    setIsConfirmDiscardDialogOpen(true)
  }, [close, hasUnsavedChanges, isSaving])

  const handleCscfVariableChange = useCallback((value: number | undefined) => {
    setCscfVariable(value ?? 0)
  }, [])

  const handleQuarterChange = useCallback(
    (
      quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4',
      field: EditableEuaQuarterField,
      value: string
    ) => {
      setModifiers((prev) => {
        const nextQuarter = { ...prev.euaPriceQuarters[quarter] }
        if (field === 'value') {
          nextQuarter.value = value === '' ? null : (parseFloat(value) ?? null)
        } else {
          nextQuarter[field] = value
        }
        return {
          ...prev,
          euaPriceQuarters: {
            ...prev.euaPriceQuarters,
            [quarter]: nextQuarter,
          },
        }
      })
    },
    []
  )

  const handleApplyOfficialPrice = useCallback(
    async (quarter: 'Q1' | 'Q2' | 'Q3') => {
      const refetchByQuarter = {
        Q1: refetchOfficialQ1Price,
        Q2: refetchOfficialQ2Price,
        Q3: refetchOfficialQ3Price,
      } as const
      const result = await refetchByQuarter[quarter]()
      if (result.error) {
        toast.error(
          t('cbamModifiers:editVariables.notifications.officialPriceFetchError')
        )
        return
      }
      const data = result.data
      const raw = data?.value
      if (raw == null || raw === '') {
        toast.error(
          t('cbamModifiers:editVariables.notifications.officialPriceFetchError')
        )
        return
      }
      const parsed = parseFloat(raw)
      const value = Number.isNaN(parsed) ? null : parsed
      setModifiers((prev) => ({
        ...prev,
        euaPriceQuarters: {
          ...prev.euaPriceQuarters,
          [quarter]: {
            ...prev.euaPriceQuarters[quarter],
            value,
            lowPercentage: '0',
            highPercentage: '0',
            isOfficialValueLocked: true,
          },
        },
      }))
      toast.success(
        t('cbamModifiers:editVariables.notifications.officialPriceApplied')
      )
    },
    [refetchOfficialQ1Price, refetchOfficialQ2Price, refetchOfficialQ3Price, t]
  )

  const handleApplyLatestEuaPrice = useCallback(async () => {
    const result = await refetchEuaByQuarter()
    const data = result.data
    if (!data?.quarters) {
      return
    }

    setModifiers((prev) => {
      const newModifiers = {
        ...prev,
        euaPriceQuarters: { ...prev.euaPriceQuarters },
      }
      for (const quarter of data.quarters) {
        const key = quarter.value
        if (newModifiers.euaPriceQuarters[key].isOfficialValueLocked) {
          continue
        }
        const value = quarter.averagePrice
          ? parseFloat(quarter.averagePrice)
          : null
        newModifiers.euaPriceQuarters[key] = {
          ...newModifiers.euaPriceQuarters[key],
          value,
        }
      }
      return newModifiers
    })
    toast.success(
      t('cbamModifiers:editVariables.notifications.latestEuaPriceApplied')
    )
  }, [refetchEuaByQuarter, t])

  const handleSubmit = useCallback(async () => {
    const payloadModifiers: CbamModifiers = {
      cscfPercentage: isCscfLocked ? '100' : String(appliedCscf),
      euaPriceQuarters: {
        ...modifiers.euaPriceQuarters,
        Q1: {
          ...modifiers.euaPriceQuarters.Q1,
          value: euaQuarterValueToPayloadString(
            modifiers.euaPriceQuarters.Q1.value
          ),
          isOfficialValueLocked:
            modifiers.euaPriceQuarters.Q1.isOfficialValueLocked,
        },
        Q2: {
          ...modifiers.euaPriceQuarters.Q2,
          value: euaQuarterValueToPayloadString(
            modifiers.euaPriceQuarters.Q2.value
          ),
          isOfficialValueLocked:
            modifiers.euaPriceQuarters.Q2.isOfficialValueLocked,
        },
        Q3: {
          ...modifiers.euaPriceQuarters.Q3,
          value: euaQuarterValueToPayloadString(
            modifiers.euaPriceQuarters.Q3.value
          ),
          isOfficialValueLocked:
            modifiers.euaPriceQuarters.Q3.isOfficialValueLocked,
        },
        Q4: {
          ...modifiers.euaPriceQuarters.Q4,
          value: euaQuarterValueToPayloadString(
            modifiers.euaPriceQuarters.Q4.value
          ),
          isOfficialValueLocked:
            modifiers.euaPriceQuarters.Q4.isOfficialValueLocked,
        },
      },
    }
    try {
      await onSave(payloadModifiers)
      close()
    } catch (error) {
      console.error(error)
    }
  }, [appliedCscf, isCscfLocked, modifiers, onSave, close])

  return (
    <>
      <Drawer isOpen={isOpen} close={requestClose} sizeType="3xl">
        <div className="flex flex-col h-full">
          <div className="shrink-0 p-4">
            <div className="flex items-center justify-between">
              <H2>{t('cbamModifiers:editVariables.title')}</H2>
              <IconButton
                variant="neutral"
                icon={<RightArrowIcon />}
                onClick={requestClose}
                aria-label={t('common:actions.close')}
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            <Alert type="warning" className="text-sm">
              {t('cbamModifiers:editVariables.disclaimer')}
            </Alert>

            <section>
              <h3 className="font-medium mb-3">
                {t('cbamModifiers:editVariables.cscfSectionTitle')}
              </h3>
              <div className="mb-3">
                <InfoDisclosure
                  titleKey="cbamModifiers:editVariables.moreDetails"
                  contentKey="cbamModifiers:editVariables.infoDialog.cscf.content"
                />
              </div>
              <div className="grid grid-cols-3 gap-x-6 gap-y-2 items-end w-2/3">
                <div className="">
                  <span className="text-gray-600 text-sm">
                    {t('cbamModifiers:editVariables.base')}
                  </span>
                </div>
                <div className="">
                  <span className="text-gray-600 text-sm">
                    {t('cbamModifiers:editVariables.variable')}
                  </span>
                </div>
                <div className="">
                  <span className="text-gray-600 text-sm">
                    {t('cbamModifiers:editVariables.applied')}
                  </span>
                </div>
                <div className=" flex items-center h-full">
                  <span className="font-medium">100%</span>
                </div>
                <div className="max-w-20">
                  <FieldRangeCaret
                    value={cscfVariable}
                    onChange={handleCscfVariableChange}
                    controls={{ min: 0, max: 100, step: 1 }}
                    disabled={isCscfLocked}
                  />
                </div>
                <div className="flex items-center h-full">
                  <span className="font-medium">{appliedCscf.toFixed(0)}%</span>
                </div>
              </div>
            </section>

            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-medium">
                  {t('cbamModifiers:editVariables.euaSectionTitle')}
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="neutral"
                    onClick={handleApplyLatestEuaPrice}
                    sizeType="xs"
                    icon={<SyncIcon className="size-6" />}
                  >
                    {t('cbamModifiers:editVariables.applyLatestAveragePrice')}
                  </Button>
                  <InfoTooltip
                    content={
                      <span className="block text-left max-w-sm">
                        <Trans
                          i18nKey="cbamModifiers:editVariables.infoDialog.applyLatestAveragePrice.content"
                          components={infoContentComponents}
                        />
                      </span>
                    }
                    position="top"
                    size="medium"
                  />
                </div>
              </div>
              <div className="mb-3">
                <InfoDisclosure
                  titleKey="cbamModifiers:editVariables.moreDetails"
                  contentKey="cbamModifiers:editVariables.infoDialog.certificatePrice.content"
                />
              </div>
              <EuaCertificatePriceTable
                simulationYear={year}
                euaPriceQuarters={modifiers.euaPriceQuarters}
                onQuarterChange={handleQuarterChange}
                onApplyOfficialPrice={handleApplyOfficialPrice}
                applyingOfficialQuarter={applyingOfficialQuarter}
              />
            </section>
          </div>

          <footer className="shrink-0 p-4">
            <Button
              onClick={handleSubmit}
              disabled={isSaving || !hasUnsavedChanges}
              isLoading={isSaving}
              className="w-full"
            >
              {t('cbamModifiers:editVariables.setValues')}
            </Button>
          </footer>
        </div>
      </Drawer>
      <GenericDialog
        isOpen={isConfirmDiscardDialogOpen}
        close={() => setIsConfirmDiscardDialogOpen(false)}
        title={t('cbamModifiers:editVariables.confirmClose.title')}
        size="md"
      >
        <div className="space-y-4">
          <p>{t('cbamModifiers:editVariables.confirmClose.description')}</p>
          <footer className="gap-2 flex justify-end">
            <Button
              variant="neutral"
              onClick={() => setIsConfirmDiscardDialogOpen(false)}
            >
              {t(
                'cbamModifiers:editVariables.confirmClose.actions.keepEditing'
              )}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setIsConfirmDiscardDialogOpen(false)
                close()
              }}
            >
              {t(
                'cbamModifiers:editVariables.confirmClose.actions.discardChanges'
              )}
            </Button>
          </footer>
        </div>
      </GenericDialog>
    </>
  )
}
